import { Button, Checkbox, DialogActions, DialogContent, Divider, FormControlLabel, Link, Typography, useColorScheme } from '@mui/material';
import DialogTitle from '@mui/material/DialogTitle';
import { Stack, useMediaQuery } from '@mui/system';
import Lottie from 'lottie-react';
import { useEffect, useState } from 'react';
import { Trans } from 'react-i18next';
import { robotDataSlice } from '../../../redux/RobotDataSlice';
import { robotSlice } from '../../../redux/RobotSlice';
import { trackConnectSelected } from '../../../analytics/bpsEvents';
import { BLUEPRINT_STUDIO_CONNECTION_HELP_URL, DEFAULT_SELECTED_ROBOT, RobotActions, ROBOTS } from '../../../utils/constants';
import BPRUSBConnectionSvg from '../../Icons/Connection/bpr-usb-connection.svg';
import usbConnectionLottieDark from '../../Icons/Connection/bps-usb-connection-dark.json';
import usbConnectionLottieLight from '../../Icons/Connection/bps-usb-connection-light.json';
import { RootState } from '../../../redux/Store';
import { useDispatch, useSelector } from 'react-redux';
import { useConnectionPreferences } from '../../../hooks/storage/useConnectionPreferences';

interface RobotSerialConnectionDialogProps {
  onClose: () => void;
}

const RobotSerialConnectionDialog = ({ onClose }: RobotSerialConnectionDialogProps) => {
  const { mode, systemMode } = useColorScheme();
  let colorMode = mode;
  if (mode === 'system') {
    colorMode = systemMode;
  }
  const { userRobotColor } = useSelector((state: RootState) => state.robotData);
  const { robotState } = useSelector((state: RootState) => state.robot);
  const dispatch = useDispatch();
  const [connectionPreferences, setConnectionPreferences] = useConnectionPreferences();
  const fullRobotName = ROBOTS.BPRDU.name;

  const [imageHeight, setImageHeight] = useState<string>('160');
  const [animationHeight, setAnimationHeight] = useState<string>('256');

  // for devices like chromebooks that have wide but short screen ratios.
  const shortScreen = useMediaQuery(`(max-height: 700px)`);

  useEffect(() => {
    if (shortScreen) {
      setImageHeight('68');
      setAnimationHeight('109');
    } else {
      setImageHeight('160');
      setAnimationHeight('256');
    }
  }, [shortScreen]);

  useEffect(() => {
    if (robotState === RobotActions.STATE.CONNECTING) {
      onClose();
    }
  }, [onClose, robotState]);

  const connectRobot = () => {
    const color = userRobotColor;
    trackConnectSelected(RobotActions.STATE.CONNECTION_TYPE.SERIAL);
    dispatch(robotDataSlice.actions.onUpdateRobotType(DEFAULT_SELECTED_ROBOT));
    dispatch(
      robotSlice.actions.onUpdateRobotStateAndConnectionType({
        robotState: RobotActions.STATE.CONNECT,
        connectionType: RobotActions.STATE.CONNECTION_TYPE.SERIAL,
      })
    );
    dispatch(robotDataSlice.actions.onUpdateUserRobotColor(color));
  };

  const handleClose = () => {
    onClose();
  };

  const handleDontShowAgainChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { checked } = event.target;
    setConnectionPreferences({
      ...connectionPreferences,
      skipUsbInfo: checked,
    });
  };

  return (
    <>
      <DialogTitle textAlign="center" variant="panelTitle" sx={{ py: 1.5 }}>
        <Trans i18nKey="robotUsbConnect.title">Wired Connection</Trans>
      </DialogTitle>
      <Divider />
      <DialogContent sx={{ py: shortScreen ? 1 : 2, px: 2.5 }}>
        <Stack direction="column" spacing={2} alignItems="center">
          <Typography variant="body1">
            <Trans i18nKey={'robotUsbConnect.heading'}>Plug your {fullRobotName} into your computer with a USB C cable.</Trans>
          </Typography>
          <img
            src={BPRUSBConnectionSvg}
            alt="Swerve Drive Robot Wired Connection"
            height={imageHeight}
            style={{ filter: colorMode === 'dark' ? 'invert(1)' : 'none' }}
          />
          <Typography variant="body1">
            <Trans i18nKey={'robotUsbConnect.second_heading'}>
              Press the {"'Connect'"} button below to connect to a serial port. Then select your {fullRobotName} and choose {"'Connect'"}
            </Trans>
          </Typography>
          <Lottie
            animationData={colorMode === 'dark' ? usbConnectionLottieDark : usbConnectionLottieLight}
            loop
            style={{ height: `${animationHeight}px` }}
          />
          <Stack spacing={0.5} alignItems="center">
            <Typography variant="body1">
              <Trans i18nKey={'robotUsbConnect.having_issues'}>Having issues connecting your {fullRobotName}?</Trans>
            </Typography>
            <Link href={BLUEPRINT_STUDIO_CONNECTION_HELP_URL} target="_blank" rel="noopener noreferrer">
              <Trans i18nKey="robotConnection.learn_more">Learn More</Trans>
            </Link>
          </Stack>
        </Stack>
      </DialogContent>
      <Divider />
      <DialogActions sx={{ justifyContent: 'space-between', alignItems: 'center', py: 1.5, px: 2 }}>
        <FormControlLabel
          sx={{
            borderRadius: 2,
            px: 1.5,
            py: 0.5,
            bgcolor: 'action.hover',
            '& .MuiFormControlLabel-label': {
              fontSize: 13,
            },
          }}
          control={
            <Checkbox
              color="primary"
              size="small"
              sx={{ p: 0.5, borderRadius: 1 }}
              checked={connectionPreferences.skipUsbInfo}
              onChange={handleDontShowAgainChange}
            />
          }
          label={<Trans i18nKey="robotConnection.dont_show_again">Don't show this again</Trans>}
        />
        <Stack direction="row" spacing={2} alignItems="center" useFlexGap flexWrap="wrap">
          <Button variant="text" autoFocus onClick={handleClose}>
            <Trans i18nKey="button.cancel">Cancel</Trans>
          </Button>
          <Button variant="contained" onClick={() => connectRobot()} autoFocus>
            <Trans i18nKey="button.connect">Connect</Trans>
          </Button>
        </Stack>
      </DialogActions>
    </>
  );
};

export default RobotSerialConnectionDialog;
